import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Typography,
} from '@mui/material';
import {
    ChevronRight as ChevronRightIcon,
    Store as StoreIcon,
} from '@mui/icons-material';

import { fetchCarts } from '../../actions/cartActions';
import { formatPrice } from '../../lib/money';
import {
    COLORS,
    FONT_BODY,
    FONT_TITLE,
    secondaryButtonSx,
} from '../storefront/storefrontStyles';
import CartLayout, { panelSx } from './CartLayout';
import { cartPath } from './cartPaths';

// /carrito: un carrito abierto por tienda.
const CartList = ({ summaries, isFetching, loaded, fetchCarts }) => {
    useEffect(() => {
        fetchCarts();
    }, [fetchCarts]);

    let content;
    if (!loaded || (isFetching && summaries.length === 0)) {
        content = (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: COLORS.accent }} />
            </Box>
        );
    } else if (summaries.length === 0) {
        content = (
            <Box sx={{ ...panelSx, textAlign: 'center', py: 8 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_TITLE,
                        fontWeight: 700,
                        fontSize: '1.4rem',
                        textTransform: 'uppercase',
                        mb: 1,
                    }}
                >
                    No tenés carritos abiertos
                </Typography>
                <Typography sx={{ color: COLORS.muted, mb: 3 }}>
                    Entrá a una tienda y agregá productos para comprarlos sin
                    videollamada.
                </Typography>
                <Button
                    variant="outlined"
                    component={RouterLink}
                    to="/home"
                    sx={{ ...secondaryButtonSx, px: 4 }}
                >
                    Ver tiendas
                </Button>
            </Box>
        );
    } else {
        content = (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {summaries.map((summary) => (
                    <Box
                        key={summary.storeId}
                        component={RouterLink}
                        to={cartPath(summary.storeId)}
                        sx={{
                            ...panelSx,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            color: 'inherit',
                            textDecoration: 'none',
                            transition: 'border-color 0.2s',
                            '&:hover': { borderColor: COLORS.borderHover },
                        }}
                    >
                        <Avatar
                            src={summary.store.profileImageUrl || undefined}
                            alt={summary.store.name}
                            sx={{
                                width: 48,
                                height: 48,
                                backgroundColor: COLORS.card,
                                border: `1px solid ${COLORS.border}`,
                            }}
                        >
                            <StoreIcon sx={{ color: '#555' }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_TITLE,
                                    fontWeight: 700,
                                    fontSize: '1.15rem',
                                    textTransform: 'uppercase',
                                }}
                            >
                                {summary.store.name}
                            </Typography>
                            <Typography
                                sx={{
                                    color: COLORS.muted,
                                    fontFamily: FONT_BODY,
                                    fontSize: '0.9rem',
                                }}
                            >
                                {summary.itemCount}{' '}
                                {summary.itemCount === 1
                                    ? 'producto'
                                    : 'productos'}{' '}
                                · {formatPrice(summary.subtotal)}
                            </Typography>
                        </Box>
                        <ChevronRightIcon sx={{ color: COLORS.muted }} />
                    </Box>
                ))}
            </Box>
        );
    }

    return (
        <CartLayout backTo="/home" backLabel="Inicio" title="Mis carritos">
            {content}
        </CartLayout>
    );
};

const mapStateToProps = (state) => {
    return {
        summaries: state.cart.summaries,
        isFetching: state.cart.isFetchingSummaries,
        loaded: state.cart.summariesLoaded,
    };
};

export default connect(mapStateToProps, { fetchCarts })(CartList);
